import useBreadcrumbs from '@admin/hooks/useAdminBreadcrumbs';
import AutomationRulesManager from '@admin/components/governance/AutomationRulesManager';
import ExtensionPermissionsManager from '@admin/components/governance/ExtensionPermissionsManager';
import OrgExtensionsManager from '@admin/components/governance/OrgExtensionsManager';
import OrgExtensionUsageManager from '@admin/components/governance/OrgExtensionUsageManager';
import styled from '@emotion/styled';
import { Tab, Tabs } from '@mui/material';
import PageHeader from '@shared/components/core/headers/PageHeader';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import OrgNoAccessBanner from '@shared/components/organization/banners/OrgNoAccessBanner';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import OrgPageTemplate from 'admin/components/organization/OrgPageTemplate';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type GovernanceTab = 'automation' | 'permissions' | 'skills' | 'connectors' | 'usage';

export default function OrgGovernancePage() {
    const { t } = useTranslation();
    const { items } = useBreadcrumbs();
    const { currentOrg, isLoading, canManageGovernance, canManageOrganizationSkills, canManageOrganizationMcpServers } =
        useCurrentOrgDetailsFetching();
    const [tab, setTab] = useState<GovernanceTab>('automation');

    if (!currentOrg || isLoading) return <SpacedLoader />;

    const canViewUsage = canManageOrganizationSkills || canManageOrganizationMcpServers;

    return (
        <OrgPageTemplate>
            <PageHeader
                title={t('common:organization.governance.orgTitle', { value: currentOrg.name })}
                description={t('common:organization.governance.description')}
                items={items}
            />
            {canManageGovernance ? (
                <>
                    <StyledTabs
                        value={tab}
                        onChange={(_, value) => setTab(value as GovernanceTab)}
                        variant="scrollable"
                        scrollButtons="auto">
                        <Tab value="automation" label={t('common:organization.governance.tabs.automation')} />
                        <Tab value="permissions" label={t('common:organization.governance.tabs.permissions')} />
                        <Tab value="skills" label={t('common:organization.governance.tabs.skills')} />
                        <Tab value="connectors" label={t('common:organization.governance.tabs.connectors')} />
                        {canViewUsage && <Tab value="usage" label={t('common:organization.governance.tabs.usage')} />}
                    </StyledTabs>

                    {tab === 'automation' && <AutomationRulesManager organizationId={currentOrg.id} />}
                    {tab === 'permissions' && <ExtensionPermissionsManager organization={currentOrg} />}
                    {tab === 'skills' && (
                        <OrgExtensionsManager
                            organizationId={currentOrg.id}
                            kind="skills"
                            canManage={canManageOrganizationSkills}
                        />
                    )}
                    {tab === 'connectors' && (
                        <OrgExtensionsManager
                            organizationId={currentOrg.id}
                            kind="connectors"
                            canManage={canManageOrganizationMcpServers}
                        />
                    )}
                    {tab === 'usage' && canViewUsage && (
                        <OrgExtensionUsageManager
                            organizationId={currentOrg.id}
                            canViewSkills={canManageOrganizationSkills}
                            canViewConnectors={canManageOrganizationMcpServers}
                        />
                    )}
                </>
            ) : (
                <OrgNoAccessBanner />
            )}
        </OrgPageTemplate>
    );
}

const StyledTabs = styled(Tabs)`
    border-bottom: 1px solid ${(props) => props.theme.color.border};
    margin-top: 1rem;
`;
